if (!customElements.get('static-sibling-color-picker')) {
  customElements.define(
    'static-sibling-color-picker',
    class StaticSiblingColorPicker extends HTMLElement {
      connectedCallback() {
        this.onClick = this.onClick?.bind(this) || this.handleClick.bind(this);
        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        if (this.onClick) this.removeEventListener('click', this.onClick);
      }

      getSectionId() {
        const productInfo = this.closest('product-info');
        return productInfo?.dataset?.section;
      }

      getCurrentSelectedOptions() {
        const productInfo = this.closest('product-info');
        const variantRadios = productInfo?.querySelector('variant-radios[data-variant-picker-context="main"], variant-radios:not([data-variant-picker-context])');
        const variantSelects = productInfo?.querySelector('variant-selects[data-variant-picker-context="main"], variant-selects:not([data-variant-picker-context])');

        const getOptionsFromFieldsets = (root) => {
          const fieldsets = Array.from(root.querySelectorAll('fieldset'));
          return fieldsets.map((fieldset) => {
            const checked = fieldset.querySelector('input[type="radio"]:checked');
            return checked ? checked.value : null;
          });
        };

        const getOptionsFromSelects = (root) => {
          const selects = Array.from(root.querySelectorAll('select'));
          return selects.map((select) => select.value || null);
        };

        if (variantRadios) return getOptionsFromFieldsets(variantRadios);
        if (variantSelects) return getOptionsFromSelects(variantSelects);
        return [];
      }

      async resolveStaticVariantId(productUrl, desiredOptions, colorPosition, clickedColorValue) {
        const matchMode = this.dataset.matchMode || 'match_options';

        const res = await fetch(`${productUrl}.js`, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`Failed to fetch product JSON for ${productUrl}`);
        const data = await res.json();
        const variants = data?.variants || [];
        if (!variants.length) return null;

        const normalized = (opts) => {
          const next = Array.isArray(opts) ? [...opts] : [];
          const idx = Math.max(0, (parseInt(colorPosition, 10) || 1) - 1);
          next[idx] = clickedColorValue;
          return next.filter((v) => v !== null && v !== undefined && v !== '');
        };

        const wanted = normalized(desiredOptions);

        // 1) Try exact option match (best UX, keeps size when possible)
        if (matchMode === 'match_options' && wanted.length) {
          const matched = variants.find((v) => {
            const vOpts = [v.option1, v.option2, v.option3].filter(Boolean);
            if (vOpts.length < wanted.length) return false;
            return wanted.every((opt, idx) => vOpts[idx] === opt);
          });
          if (matched) return matched.id;
        }

        // 2) Fallback: pick first available variant for this color
        const pos = Math.max(1, parseInt(colorPosition, 10) || 1);
        const colorMatches = variants.filter((v) => v[`option${pos}`] === clickedColorValue);
        const firstAvailable = colorMatches.find((v) => v.available);
        return (firstAvailable || colorMatches[0] || variants.find((v) => v.available) || variants[0])?.id || null;
      }

      async swapMainProductSection(productUrl, variantId) {
        const sectionId = this.getSectionId();
        if (!sectionId) throw new Error('Missing section id');

        const currentSection = document.getElementById(`MainProduct-${sectionId}`);
        if (!currentSection) throw new Error('Could not find current MainProduct section');

        const res = await fetch(`${productUrl}?variant=${variantId}&section_id=${sectionId}`, {
          credentials: 'same-origin'
        });
        if (!res.ok) throw new Error(`Failed to fetch section HTML for ${productUrl}`);

        const htmlText = await res.text();
        const html = new DOMParser().parseFromString(htmlText, 'text/html');
        const updatedSection = html.getElementById(`MainProduct-${sectionId}`);
        if (!updatedSection) throw new Error('Fetched HTML did not include expected MainProduct section');

        currentSection.replaceWith(updatedSection);

        document.dispatchEvent(
          new CustomEvent('main-product-section:replaced', {
            bubbles: true,
            detail: { sectionId }
          })
        );

        const nextUrl = `${productUrl}?variant=${variantId}`;
        window.history.pushState({ variantId, productUrl }, '', nextUrl);
      }

      async handleClick(event) {
        const btn = event.target.closest('[data-static-color-value]');
        if (!btn || !this.contains(btn)) return;
        event.preventDefault();

        const productUrl = this.dataset.staticProductUrl;
        const colorPosition = this.dataset.colorOptionPosition || '1';
        const clickedColorValue = btn.dataset.staticColorValue;
        if (!productUrl || !clickedColorValue) return;

        try {
          this.classList.add('is-loading');
          const currentOptions = this.getCurrentSelectedOptions();
          const variantId = await this.resolveStaticVariantId(
            productUrl,
            currentOptions,
            colorPosition,
            clickedColorValue
          );
          if (!variantId) return;
          await this.swapMainProductSection(productUrl, variantId);
        } catch (e) {
          console.error(e);
        } finally {
          this.classList.remove('is-loading');
        }
      }
    }
  );
}

